import React, { useEffect, useState } from 'react';
import { Button, Paper } from '@mui/material';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Company, CompanyService } from 'us-web-services';
import CompanyEditForm from './CompanyEditForm';
import CompanyBenefits from './CompanyBenefits';
import CompanyDomains from './CompanyDomains';
import CompanySubscription from './CompanySubscription';
import PageStyles from '../../../styles/PageStyles';
import useCompany from '../../../store/useCompany';
import CompanySafelist from './CompanySafelist';
import CompanyEmployeeToggle from './CompanyEmployeeToggle';
import CompanyTerminate from './CompanyTerminate';
import CompanyCensus from './CompanyCensus/CompanyCensus';
import CompanyPartners from './CompanyPartners';
import CompanyEmployerAdmins from './CompanyEmployerAdmins';

function CompanyEdit(props: RouteComponentProps<{ id: string }>) {
  const [companyState, companyActions] = useCompany();
  const [company, setCompany] = useState<Company>(companyState.company || {});
  const [isLoading, setIsLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [hasValidationError, setHasValidationError] = useState(false);

  const { classes } = PageStyles();
  const { match, history } = props;

  useEffect(() => {
    const companyId = Number.parseInt(match.params.id, 10);

    const getCompany = async () => {
      try {
        const response = await CompanyService.get(companyId);
        const companyData = response.data.data;

        setCompany(companyData);
        setIsLoading(false);
      } catch {
        setCompany({});
        setIsLoading(false);
      }
    };

    if (companyId > 0) {
      // If the currently loaded company already matches the route id and this
      // is not an explicit refresh, avoid an unnecessary fetch and loading state.
      if (company.id === companyId && refreshKey === 0) {
        setIsLoading(false);

        return;
      }
      getCompany();
    } else {
      setIsLoading(false);
    }
  }, [company.id, match.params.id, refreshKey]);

  const refreshCompany = () => {
    setRefreshKey(prev => prev + 1);
  };

  const saveCompany = async () => {
    try {
      if (company.id > 0) {
        const companyData = {
          id: company.id,
          name: company.name,
          displayName: company.displayName,
          alias: company.alias,
          censusKey: company.censusKey,
          policyUrl: company.policyUrl,
          landingPageUrl: company.landingPageUrl,
          useExternalId: company.useExternalId,
          emailValidationEnabled: company.emailValidationEnabled,
          verificationRequired: company.verificationRequired,
          platformFeeWaived: company.platformFeeWaived,
          zendeskEnabled: company.zendeskEnabled,
        };

        const response = await CompanyService.patch(companyData.id, companyData);

        companyActions.set(response.data);
      } else {
        const companyData = {
          name: company.name,
          displayName: company.displayName,
          alias: company.alias,
          censusKey: company.censusKey,
          policyUrl: company.policyUrl,
          useExternalId: company.useExternalId,
          emailValidationEnabled: company.emailValidationEnabled,
          verificationRequired: company.verificationRequired,
          platformFeeWaived: company.platformFeeWaived,
        };

        const response = await CompanyService.create(companyData);

        companyActions.set(response.data);
      }

      toast.success('Company updated successfully');

      history.push(`/companies?id=${company.id}`);
    } catch (error) {
      toast.error(
        error?.response?.data?.errors?.[0]?.message ?? 'Failed to save company. Please try again.',
      );
    }
  };

  const cancelEdit = async () => {
    history.push(`/companies?id=${company.id}`);
  };

  return (
    <>
      <Paper className={classes.paper}>
        {company.id && !isLoading && (
          <>
            <CompanyEditForm company={company} setCompany={setCompany} setHasValidationError={setHasValidationError} />
            <div className={classes.buttons}>
              <Button
                variant='contained'
                color='secondary'
                onClick={cancelEdit}
                className={classes.button}
              >
                Cancel
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={saveCompany}
                className={classes.button}
                disabled={hasValidationError}
              >
                Save
              </Button>
            </div>
          </>
        )}
        {!company.id && (
          isLoading ? 'Loading...' : 'Company not found.'
        )}
      </Paper>
      {company.id && !isLoading && (
        <>
          <CompanySubscription company={company} />
          <CompanyEmployerAdmins company={company} />
          <CompanyDomains company={company} />
          <CompanyBenefits company={company} />
          <CompanySafelist company={company} refreshKey={refreshKey} />
          <CompanyPartners company={company} />
          <CompanyEmployeeToggle company={company} />
          <CompanyCensus company={company} />
          <CompanyTerminate company={company} onTerminate={refreshCompany} />
        </>
      )}
    </>
  );
}

export default withRouter(CompanyEdit);
